import {
  Box,
  Slider,
  Switch,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { flexCenter } from '../../commonUI/cssClasses';
import {
  selectHSVSaturation,
  selectHSVValue,
  selectUseCustomHSVSaturation,
  selectUseCustomHSVValue,
  setHSVSaturation,
  setHSVValue,
  setUseCustomHSVSaturation,
  setUseCustomHSVValue,
} from '../../map/mapSlice';
interface Props {
  commonToggleWithLabelStyles: SxProps;
}

const CustomHSVColorSettings: React.FC<Props> = ({
  commonToggleWithLabelStyles,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const useCustomHSVSaturation = useAppSelector(selectUseCustomHSVSaturation);
  const HSVSaturation = useAppSelector(selectHSVSaturation);
  const useCustomHSVValue = useAppSelector(selectUseCustomHSVValue);
  const HSVValue = useAppSelector(selectHSVValue);
  const { t } = useTranslation();

  const handleSaturationChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    if (typeof newValue === 'number') {
      dispatch(setHSVSaturation(newValue));
    }
  };

  const handleValueChange = (event: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      dispatch(setHSVValue(newValue));
    }
  };

  const handleUseCustomHSVSaturationClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    dispatch(setUseCustomHSVSaturation(event.target.checked));
  };

  const handleUseCustomHSVValueClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    dispatch(setUseCustomHSVValue(event.target.checked));
  };

  const sliderStyles: SxProps = {
    width: '80%',
    '&.Mui-disabled': {
      opacity: 0.3,
      color: theme.colors.darkTheme.grey.muted,
    },
  };

  return (
    <Box sx={{ width: '100%', ...flexCenter, flexDirection: 'column' }}>
      <Box
        sx={{
          width: '100%',
          ...flexCenter,
          flexDirection: 'column',
        }}
      >
        <Box sx={{ ...commonToggleWithLabelStyles }}>
          <Typography noWrap>
            {t('Custom Saturation', { ns: 'ranch-view' })}
          </Typography>
          <Switch
            checked={useCustomHSVSaturation}
            onChange={handleUseCustomHSVSaturationClick}
            inputProps={{ 'aria-label': 'Custom HSV Toggle' }}
          />
        </Box>
        <Typography noWrap>{t('Saturation', { ns: 'ranch-view' })}</Typography>
        <Slider
          value={HSVSaturation}
          aria-label='Saturation'
          defaultValue={HSVSaturation}
          disabled={!useCustomHSVSaturation}
          valueLabelDisplay='auto'
          step={1}
          min={0}
          max={100}
          onChange={handleSaturationChange}
          sx={sliderStyles}
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          ...flexCenter,
          flexDirection: 'column',
        }}
      >
        <Box sx={{ ...commonToggleWithLabelStyles }}>
          <Typography noWrap sx={{ m: 0, mr: '0.5rem' }}>
            {t('Custom Brightness', { ns: 'ranch-view' })}
          </Typography>
          <Switch
            checked={useCustomHSVValue}
            onChange={handleUseCustomHSVValueClick}
            inputProps={{ 'aria-label': 'Custom HSV Toggle' }}
          />
        </Box>
        <Typography noWrap sx={{ m: 0, mr: '0.5rem' }}>
          {t('Brightness', { ns: 'ranch-view' })}
        </Typography>
        <Slider
          value={HSVValue}
          aria-label='Brightness'
          defaultValue={HSVValue}
          disabled={!useCustomHSVValue}
          valueLabelDisplay='auto'
          step={1}
          min={0}
          max={100}
          onChange={handleValueChange}
          sx={sliderStyles}
        />
      </Box>
    </Box>
  );
};

export default CustomHSVColorSettings;
